
import './ProjectDetail.css'; // Assuming you'll reuse the same CSS
import {
  ProjectFullWidth,
  ProjectSideBySide,
  ProjectSideBySideReverse,
  ProjectFooter
} from './ProjectCommon';
import projects from './projects';

function RootsProjectDetail() {
  // Get project data from projects.js
  const projectData = projects.find(p => p.id === 'rooted_in_place');

  const footerProps = {
    title: "Rooted in Place",
    description: projectData ? projectData.description : "",
    collaborators: [
      {
        name: "Hana Khurshid",
        url: "https://www.linkedin.com/in/hana-khurshid/"
      },
      {
        name: "Isabelle Lee",
        url: "https://www.linkedin.com/in/isabelle-lee-cy/"
      }
    ],
    backgroundImage: "/rooted_in_place/lattice_closeup.webp"
  };

  return (
    <div className="project-detail-root">
      <div className="project-detail-hero">
        <img src="/rooted_in_place/hero.webp" alt="Rooted in Place - Living Architecture" className="project-detail-hero-img" />
      </div>
      <div className="project-detail-content">
        <div className="project-detail-intro">
          <div className="project-detail-title">
            <h1>Rooted in Place: Building with Living Systems</h1>
            <p className="project-credit">
              A project by Luke Fiorante, <a href="https://www.linkedin.com/in/hana-khurshid/" target="_blank" rel="noopener noreferrer" className="project-link">Hana Khurshid</a>, and <a href="https://www.linkedin.com/in/isabelle-lee-cy/" target="_blank" rel="noopener noreferrer" className="project-link">Isabelle Lee</a>
            </p>
          </div>
          <p>
            <b>Rooted in Place</b> is a <b>speculative architectural experiment</b> that explores the future potential of bio-fabrication. Through material research and hands-on fabrication, we investigated how living plant root systems could be guided to create architectural components. The project uses <b>3D-printed scaffolds</b> to direct biological growth, imagining a future where structures are not just built, but actively grown in partnership with living systems.
          </p>
        </div>
        <div className="project-detail-process">
          <ProjectFullWidth image="/rooted_in_place/process.webp" alt="Experimentation process">
            <h2>Concept</h2>
            <p>
              This project was driven by a speculative question: <b>What if our buildings could grow, adapt, and integrate with their ecosystems?</b> Instead of relying solely on inert, energy-intensive materials, we wanted to explore a symbiotic alternative. Our goal was not to develop a market-ready material, but to spark imagination and challenge conventional building paradigms by making the intelligent, adaptive behavior of root systems a visible and inspirational part of the design process.
            </p>
          </ProjectFullWidth>

          <ProjectSideBySide image="/rooted_in_place/lattices.webp" alt="3D printed lattice tiles for root growth" caption="3D-printed scaffolds designed to guide root growth.">
            <h2>Method: Design & Fabrication</h2>
            <p>
              Our experimental process began with design and fabrication. We designed a series of modular, 3D-printed lattices in Rhinoceros 3D to serve as guides for root growth, systematically testing parameters like lattice porosity, channel size, and layer height to see how geometry influenced the roots' path. These scaffolds were then fabricated using an FDM 3D printer with PLA filament, which allowed for rapid iteration.
            </p>
          </ProjectSideBySide>

          <ProjectSideBySideReverse image="/rooted_in_place/lattice_roots.webp" alt="Close-up of wheatgrass roots intertwining with a 3D printed tile" caption="Wheatgrass roots binding 3D-printed tiles into a living mat.">
            <h2>Method: Cultivation & Observation</h2>
            <p>
              The next phase involved cultivation and observation. We conducted controlled growth experiments using mung beans and wheatgrass, chosen for their distinct root structures. The seeds were cultivated within the scaffolds using an agar solution as a nutrient-rich, transparent growth medium. This setup enabled direct observation of how the roots interacted with the printed geometries over time.
            </p>
          </ProjectSideBySideReverse>

          <ProjectFullWidth image="/rooted_in_place/systems_map.webp" alt="Animated Systems Map for Rooted in Place" caption="A systems map tracing the journey from biosphere to living construction.">
            <h2>Systems-Level Analysis</h2>
            <p>
              To contextualize our material experiments, we developed a comprehensive systems map. This visual diagram traces the lifecycle of conventional building materials and contrasts it with our proposed bio-fabrication workflow. The map highlights the potential for plant-based construction methods to reduce waste, sequester carbon, and create a more symbiotic relationship between the built environment and natural ecosystems.
            </p>
          </ProjectFullWidth>

          <ProjectSideBySide image="/rooted_in_place/future_vision.webp" alt="Vision of a re-wilded urban environment with living structures" caption="A vision for rewilded cities, where buildings and nature thrive together.">
            <h2>Findings & Provocation</h2>
            <p>
              Our observations yielded several insights into the interaction between roots and scaffolds. We found that the fibrous root system of wheatgrass was particularly effective at creating a dense, interwoven mat, successfully binding multiple scaffold tiles into a single, cohesive unit. While no structural testing was performed, this visual result highlights a compelling possibility for living joinery. The primary outcome is a tangible artifact that serves as a provocation, inspiring new ideas about how to partner with living systems in design.
            </p>
          </ProjectSideBySide>
        </div>

      </div>
      <ProjectFooter {...footerProps} />
    </div>
  );
}

export default RootsProjectDetail;